/**
 * authedFile.js — Wave 5.9 build 13.5 (Feb 2026), Fix Round v6 (2026-07-12).
 *
 * === Why this module exists ===
 * Bare `<iframe src="/api/…">` / `<img src="/api/…">` / `window.open("/api/…")`
 * cannot attach an Authorization header. When those URLs point at
 * auth-gated backend routes (e.g. `/api/files/documents/{filename}`),
 * the request lands as 401 and the viewer renders "Not authenticated"
 * as JSON. The fix is to `fetch()` the file WITH the JWT bearer header,
 * turn the response body into a Blob, and hand the caller a same-origin
 * `blob:` URL that iframes/images/download-anchors can consume.
 *
 * === Fix Round v6 (2026-07-12) — one function, no divergence ===
 * The drawing viewer worked on prod; the drawing THUMBNAIL failed to
 * fetch the same file. Two fetch paths had diverged:
 *   • viewer     : useAuthedFileUrl(url) → fetchAuthedFileBlobUrl(url) → blob:URL for iframe
 *   • thumbnail  : fetchAuthedFileBlobUrl(url) → blob:URL, then `fetch(blob:URL)` to
 *                  re-extract the bytes. For external Dropbox URLs the return
 *                  value was the ABSOLUTE URL (not a blob:), and the second
 *                  fetch failed on CORS. Also `_authHeader` only read
 *                  `accessToken` while the rest of the codebase falls back to
 *                  `token`, so cross-tab sessions could 401 the thumbnail.
 *
 * The rewrite collapses BOTH paths onto one internal `_authedFetchBlob(url)`
 * that returns the raw Blob plus diagnostic metadata (final URL used, HTTP
 * status, whether an auth token was attached, whether the URL was classified
 * as backend). `fetchAuthedFileBlobUrl` wraps that in a blob:URL. The new
 * exported `fetchDrawingFileBlob(drawing)` returns the Blob directly for
 * callers that need bytes (the thumbnail renderer). They can never diverge
 * again because both live in this file and share `_authedFetchBlob`.
 */

import { useEffect, useRef, useState } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

function _authToken() {
  if (typeof window === 'undefined' || !window.localStorage) return null;
  // Match the fallback pattern used across the codebase (see
  // ContactDetailDrawer, MultiGroupPicker, DrawingControlPage): try
  // `accessToken` first, then `token`. Pre-v6 authedFile read only
  // `accessToken`, which 401'd sessions whose token had been mirrored
  // to the legacy `token` key.
  return window.localStorage.getItem('accessToken') || window.localStorage.getItem('token') || null;
}

function _contractorSessionToken() {
  if (typeof window === 'undefined' || !window.sessionStorage) return null;
  // Same key ContractorPortal.js's contractorAxios interceptor uses.
  return window.sessionStorage.getItem('contractor_session_token') || null;
}

function _isContractorPortalPath(url) {
  // Either a bare backend path or an absolute URL under our backend.
  if (!url) return false;
  if (url.startsWith('/api/contractor-portal/')) return true;
  if (BACKEND_URL && url.startsWith(`${BACKEND_URL}/api/contractor-portal/`)) return true;
  return false;
}

function _authHeader() {
  const t = _authToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

/**
 * Resolve a possibly-relative backend URL to an absolute URL.
 * External URLs (http/https/data:) pass through unchanged.
 */
export function resolveBackendUrl(url) {
  if (!url) return '';
  if (url.startsWith('http') || url.startsWith('data:') || url.startsWith('blob:')) {
    return url;
  }
  return `${BACKEND_URL}${url.startsWith('/') ? '' : '/'}${url}`;
}

/**
 * Internal — the ONE function that fetches file bytes for any auth-gated
 * or external URL used in this app. Every drawing/document byte fetch in
 * the app flows through here.
 *
 * Returns:
 *   {
 *     blob:      Blob | null,
 *     url:       string  (final absolute URL fetched — for debug overlays),
 *     status:    number | null,
 *     hadToken:  boolean (was a bearer token attached?),
 *     isBackend: boolean (was the URL classified as our backend?),
 *     error:     string | null   ('no-url', 'http-401', 'network:…', null on success),
 *   }
 */
async function _authedFetchBlob(url) {
  if (!url) {
    return { blob: null, url: '', status: null, hadToken: false, isBackend: false, error: 'no-url' };
  }
  const abs = resolveBackendUrl(url);
  const isBackend = url.startsWith('/api/')
    || (BACKEND_URL && abs.startsWith(BACKEND_URL) && abs.includes('/api/'));
  // Stage D (2026-07-13) — contractor portal file URLs authenticate
  // via `X-Contractor-Session` (sessionStorage), NOT the tenant JWT.
  // If we're fetching a /api/contractor-portal/* path and a contractor
  // session token exists, use that instead of the bearer token so
  // DrawingCard's fetchDrawingFileBlob works inside the portal.
  const isContractorPath = _isContractorPortalPath(url);
  const contractorTok = isContractorPath ? _contractorSessionToken() : null;
  const token = contractorTok ? null : _authToken();
  const hadToken = (isContractorPath ? !!contractorTok : !!token) && !!isBackend;
  const headers = {};
  if (contractorTok) headers['X-Contractor-Session'] = contractorTok;
  else if (token) headers['Authorization'] = `Bearer ${token}`;
  try {
    const resp = await fetch(abs, { headers });
    if (!resp.ok) {
      console.warn(`authedFile: ${abs.slice(0, 80)} → HTTP ${resp.status} (hadToken=${hadToken}, isBackend=${isBackend})`);
      return { blob: null, url: abs, status: resp.status, hadToken, isBackend, error: `http-${resp.status}` };
    }
    const blob = await resp.blob();
    return { blob, url: abs, status: resp.status, hadToken, isBackend, error: null };
  } catch (err) {
    const msg = err && err.message ? String(err.message) : String(err);
    console.warn(`authedFile: fetch failed for ${abs.slice(0, 80)} (hadToken=${hadToken}) —`, msg);
    return { blob: null, url: abs, status: null, hadToken, isBackend, error: `network:${msg}` };
  }
}

/**
 * Public — the ONE shared drawing byte-fetch. Called by BOTH the viewer
 * pipeline (via useAuthedFileUrl → fetchAuthedFileBlobUrl → _authedFetchBlob)
 * AND the thumbnail renderer (directly, so it doesn't need to re-fetch the
 * blob URL). This is what stops the two paths from ever diverging again.
 *
 * Callers should destructure {blob, url, status, hadToken, error} — the
 * failure metadata is what powers the debug overlay on the thumbnail cards
 * so a prod failure can never be a blind "render failed" again.
 */
export async function fetchDrawingFileBlob(drawing) {
  // v6.1 (2026-07-12) — READ the EXACT SAME FIELD the viewer reads.
  //   DrawingControlPage.js:289 → useAuthedFileUrl(viewingDrawing?.current_file_url)
  // No fallback to versions[-1].file_url — that field stores a per-version
  // filename that on prod does NOT match the actual stored file (it looks
  // like `drawing_{pid}_{did}_v2.pdf` — a constructor artifact, not the
  // real Dropbox object). The viewer proves current_file_url is the field
  // that resolves correctly. If it's empty, that is the legitimate
  // "No file — upload v1" case (red badge).
  const url = drawing?.current_file_url || null;
  return _authedFetchBlob(url);
}

/**
 * Fetch an auth-gated file and return a same-origin `blob:` URL that
 * `<iframe>` / `<img>` / anchor `href` can consume.
 *
 * Callers MUST call `URL.revokeObjectURL(blobUrl)` when done, or use
 * the `useAuthedFileUrl` hook which does that automatically on unmount.
 *
 * Returns null on any failure (network, 401, wrong content-type).
 */
export async function fetchAuthedFileBlobUrl(url, { mimeOverride } = {}) {
  if (!url) return null;
  const abs = resolveBackendUrl(url);
  // External URLs (Dropbox share links, Unsplash, etc.) don't need our
  // JWT and don't accept it — pass through as-is for consumers that
  // just need an iframe/img `src`. (The thumbnail renderer doesn't
  // call this function anymore — it calls fetchDrawingFileBlob, which
  // fetches bytes uniformly.)
  const isBackendPath = url.startsWith('/api/')
    || (BACKEND_URL && abs.startsWith(BACKEND_URL) && abs.includes('/api/'));
  if (!isBackendPath) {
    return abs;
  }
  const res = await _authedFetchBlob(url);
  if (!res.blob) return null;
  const blob = mimeOverride ? new Blob([res.blob], { type: mimeOverride }) : res.blob;
  return URL.createObjectURL(blob);
}

/**
 * React hook. Resolves `url` (backend path) to a `blob:` URL, cleaning
 * up automatically on unmount or when the URL changes. Suitable for
 * <iframe src={blobUrl}> / <img src={blobUrl}>.
 *
 * Return shape: { blobUrl, loading, error }
 */
export function useAuthedFileUrl(url) {
  const [state, setState] = useState({ blobUrl: null, loading: !!url, error: null, status: null });
  const prevBlobRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    if (!url) {
      setState({ blobUrl: null, loading: false, error: null, status: null });
      return () => {};
    }
    setState((s) => ({ ...s, loading: true, error: null, status: null }));
    (async () => {
      // v7.1 — go through _authedFetchBlob so we can surface the actual
      // HTTP status ("File not found (404)" vs "Authentication failed
      // (401)") instead of guessing "your session may have expired".
      const res = await _authedFetchBlob(url);
      if (cancelled) return;
      let blobUrl = null;
      if (res.blob) {
        blobUrl = URL.createObjectURL(res.blob);
      }
      // Revoke any previous blob URL we handed out.
      if (prevBlobRef.current && prevBlobRef.current.startsWith('blob:')) {
        URL.revokeObjectURL(prevBlobRef.current);
      }
      prevBlobRef.current = blobUrl;
      setState({
        blobUrl,
        loading: false,
        error: blobUrl ? null : (res.error || 'load-failed'),
        status: res.status ?? null,
      });
    })();
    return () => {
      cancelled = true;
      const b = prevBlobRef.current;
      if (b && b.startsWith('blob:')) {
        setTimeout(() => URL.revokeObjectURL(b), 500);
      }
      prevBlobRef.current = null;
    };
  }, [url]);

  return state;
}

/**
 * Trigger a browser download of an auth-gated backend file. Fetches
 * with JWT header, wraps in an <a> click, revokes the blob URL after
 * the browser has taken it.
 */
export async function downloadAuthedFile(url, filename) {
  const blobUrl = await fetchAuthedFileBlobUrl(url);
  if (!blobUrl) return false;
  try {
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename || 'download';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    return true;
  } finally {
    // Give the browser a moment to start the download before revoking.
    if (blobUrl.startsWith('blob:')) {
      setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
    }
  }
}

/**
 * Open an auth-gated backend file in a new tab and trigger the browser
 * print dialog once the document has loaded. Never leaks a JWT into
 * the tab URL — the tab shows `blob:https://…/<uuid>` instead.
 */
export async function printAuthedFile(url) {
  const blobUrl = await fetchAuthedFileBlobUrl(url);
  if (!blobUrl) return false;
  const w = window.open(blobUrl, '_blank');
  if (!w) return false;
  const kick = () => {
    try { w.focus(); w.print(); } catch (_e) { /* pop-up blocker or CSP */ }
  };
  // If the tab has already loaded (blob URLs are instant), print right away;
  // otherwise wait for onload.
  if (w.document && w.document.readyState === 'complete') {
    setTimeout(kick, 100);
  } else {
    w.onload = () => setTimeout(kick, 200);
  }
  // Revoke after a generous window so the tab finishes rendering.
  setTimeout(() => { try { URL.revokeObjectURL(blobUrl); } catch (_e) { /* noop */ } }, 30000);
  return true;
}

/**
 * Open an auth-gated file in a new tab (View button). Same as Print
 * without the print() call.
 */
export async function viewAuthedFile(url) {
  const blobUrl = await fetchAuthedFileBlobUrl(url);
  if (!blobUrl) return false;
  const w = window.open(blobUrl, '_blank');
  if (!w) return false;
  setTimeout(() => { try { URL.revokeObjectURL(blobUrl); } catch (_e) { /* noop */ } }, 30000);
  return true;
}
